/**
 * The single boundary between what the API says and what a person reads.
 *
 * Server messages are written for machines and for a future offline register client: they are
 * English-only, they name schema paths (`payoutThresholds.0.thresholdMinor`), and they exist to
 * be matched on, not read aloud. Passing them straight to a toast is how a bar manager ends up
 * staring at `403` or `insufficient_role` and calling the owner.
 *
 * So the backend keeps the stable machine-readable `error` code, and the sentence a human reads
 * is chosen here. Mapping on the CODE rather than the prose is what makes that safe: the backend
 * can reword anything without silently changing what the portal displays, and when this product
 * is translated for Spanish-speaking managers, this file is the only thing that needs it.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Field-level validation issues, keyed for `form.setFieldError` — never rendered as prose. */
  readonly issues: { field: string; message: string }[];
  /**
   * The refusal's whole parsed body, for the few refusals that carry more than a code.
   *
   * Almost nothing needs it, and nothing should render it: the sentence a person reads is still
   * chosen by `errorCopy` from the code, for every reason at the top of this file. It exists
   * because a request that carries MANY things can be refused over one of them, and then the code
   * alone cannot say which — a whole payroll period submitted as one sheet is refused all or
   * nothing, and the server names the entry it was about so the screen can mark that cell rather
   * than reporting a fault over the whole month. Read the shape you expect and fall back to
   * nothing; a server that stops sending it must degrade to "the save was refused", never crash.
   */
  readonly body: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    issues: { field: string; message: string }[] = [],
    body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.issues = issues;
    this.body = body;
  }
}

/** Raised when the request never reached the server at all. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('network');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/**
 * The backend joins validation issues into one string (`"amountMinor: Required; from: Invalid"`).
 * Splitting it back apart lets the portal put each message on the field it belongs to instead of
 * in a corner toast, which is the difference between "fix this box" and "something was wrong".
 * Purely additive: an unparseable message still surfaces as a whole-form error.
 */
export function parseIssues(message: string): { field: string; message: string }[] {
  if (!message.includes(':')) return [];
  return message
    .split(';')
    .map((part) => {
      const idx = part.indexOf(':');
      if (idx <= 0) return null;
      const field = part.slice(0, idx).trim();
      const text = part.slice(idx + 1).trim();
      // Schema paths only — anything with spaces is prose, not a field name.
      if (!field || /\s/.test(field) || field === '(root)') return null;
      return { field, message: text };
    })
    .filter((x): x is { field: string; message: string } => x !== null);
}

export interface ErrorCopy {
  title: string;
  message: string;
}

/**
 * What to show a person. Deliberately says what happened and what they can do about it — never a
 * status number, and never the raw server message, which is how spec identifiers reached the
 * screen in the first place.
 */
export function errorCopy(err: unknown, action?: string): ErrorCopy {
  if (err instanceof NetworkError) {
    return {
      title: 'No connection',
      message: 'Nothing was saved. Check your internet and try again.',
    };
  }

  if (err instanceof ApiError) {
    switch (err.code) {
      case 'kds_work_pending':
        return {
          title: 'Finish the preparation tickets',
          message:
            'Bar or Kitchen still has unfinished or undelivered items. Complete them manually, or choose automatic completion when closing the drawer.',
        };
      case 'kds_station_required':
        return {
          title: 'Choose a station for this terminal',
          message: 'Select Kitchen or Bar before enabling spoken order reminders.',
        };
      case 'holiday_calendar_changed':
      case 'schedule_changed':
        return {
          title: 'Recalculate these hours',
          message:
            'The working week or holiday calendar changed. Rebuild and approve affected hours, then rebuild the pay register before signing.',
        };
      case 'settlement_currency_mismatch':
        return {
          title: 'Use the statement currency',
          message:
            'Record the bank movement in the currency of its platform statement. The statement amounts cannot be relabeled as another currency.',
        };
      case 'leave_work_conflict':
        return {
          title: 'Reconcile work and leave',
          message:
            'Work and recorded leave overlap. Correct the leave record and rebuild the hours before approving payroll.',
        };
      case 'schedule_leave_conflict':
        return {
          title: 'Reconcile leave before changing the schedule',
          message:
            'Recorded leave uses this working schedule. Reconcile or cancel that leave before editing the roster so vacation days and paid hours stay correct.',
        };
      case 'schedule_inputs_locked':
        return {
          title: 'This working week is protected',
          message:
            'Approved hours or a signed pay register use this schedule. Reopen affected records before changing it.',
        };
      case 'schedule_week_fixed':
        return {
          title: 'Choose the week in the app',
          message:
            'A saved Monday–Sunday plan keeps its dates. Use Weekly staff schedule in the app to plan a different week.',
        };
      case 'benefits_changed':
        return {
          title: 'Rebuild this pay register',
          message:
            'Leave, payment evidence, coverage or the holiday calendar changed after calculation. Rebuild and review the figures before signing.',
        };
      case 'benefits_need_review':
        return {
          title: 'Review holiday and leave pay',
          message:
            'Correct the holiday hours, verify the applicable wage basis and approve the linked leave hours before rebuilding and signing this register.',
        };
      case 'leave_needs_review':
        return {
          title: 'Review the leave record',
          message:
            'Check the dates, available statutory vacation, work schedule, supporting wage basis and certificates. An IHSS deduction needs confirmed coverage and an actual approved award.',
        };
      case 'leave_record_controls_day':
        return {
          title: 'This day belongs to a leave record',
          message:
            'Correct or cancel its record under Vacation & benefits. An ordinary hours edit cannot replace linked leave.',
        };
      case 'leave_has_payments':
        return {
          title: 'Keep the paid leave history',
          message:
            'This leave already has payment evidence and cannot be cancelled. Reconcile any correction with the recorded payment.',
        };
      case 'leave_locked':
        return {
          title: 'Reopen the affected payroll first',
          message:
            'This leave is protected by approved hours, a signed register or a recorded payment. Review that history before making a correction.',
        };
      case 'balance_before_hire':
      case 'balance_in_future':
        return {
          title: 'Check the opening balance date',
          message:
            'Use an actual reconciliation date on or after the employee’s hire date and no later than today.',
        };
      case 'opening_balance_mismatch':
        return {
          title: 'Preserve the vacation balance',
          message:
            'Use a later effective date and carry forward the recorded statutory balance. A wage or coverage update cannot reset vacation history.',
        };
      case 'owner_not_employee':
        return {
          title: 'Choose an employee',
          message: 'Statutory employee benefits cannot be entered against the owner account.',
        };
      case 'payment_in_future':
        return {
          title: 'Record the actual payment date',
          message:
            'Payment evidence must describe money already paid. Record a future payment after it happens.',
        };
      case 'advance_not_employer':
      case 'advance_not_payroll':
        return {
          title: 'Check the benefit advance',
          message:
            'An advance is a separate employer payment deducted from later payroll. Do not mark an IHSS payment or an already paid payroll register as an advance.',
        };
      case 'leave_date_requires_review':
        return {
          title: 'Reconcile this early shift with payroll',
          message:
            'The scheduled shift starts before the daily cutoff and belongs to the previous business date. This leave cannot be recorded safely until its calendar dates and payroll dates are reconciled.',
        };
      case 'benefit_profile_locked':
        return {
          title: 'Preserve the paid benefit history',
          message:
            'A finalized or paid register uses this history. Choose an effective date after its protected period and reconcile the current vacation balance.',
        };
      case 'payroll_payment_not_verified':
        return {
          title: 'Check the paid register',
          message:
            'Choose a paid register that includes this leave’s allocated benefit. The receipt must fit that share after advances and earlier receipts, in the same currency.',
        };
      case 'benefit_payment_conflict':
        return {
          title: 'Reconcile the payment evidence',
          message:
            'Check the existing receipts, remaining benefit due and currency. Cancelled leave, duplicate receipts and payments exceeding the entitlement cannot be recorded.',
        };
      case 'calendar_invalid':
        return {
          title: 'Check the holiday calendar',
          message:
            'Enter real dates and times within the selected calendar year, with each end after its start and a supporting source.',
        };
      case 'holiday_calendar_exists':
        return {
          title: 'A calendar is already recorded',
          message:
            'Review the existing complete-year holiday override. A second override cannot replace its history.',
        };
      case 'holiday_calendar_locked':
        return {
          title: 'This calendar has already been used',
          message:
            'Approved hours or a signed pay register protect this year’s holiday calendar. Reopen the affected payroll before changing it.',
        };
      case 'cash_whole_units_required':
        return {
          title: 'Whole notes only',
          message: 'Enter whole dollars or lempiras. Coins are not accepted.',
        };
      case 'closing_tips_changed':
      case 'tip_review_changed':
        return {
          title: 'Review the updated tips',
          message:
            'Tips changed while closing. Refresh End of Day and review the current employee amounts before closing the drawer.',
        };
      case 'tip_payout_changed':
        return {
          title: 'Refresh before paying tips',
          message:
            'The tips owed or available drawer cash changed. Refresh before handing over any more cash.',
        };
      case 'tip_carry_note_required':
        return {
          title: 'Account for the remaining tips',
          message:
            'Some tips are still owed. Pay them, or enter why the named employee will collect them later.',
        };
      case 'tip_employee_not_found':
        return {
          title: 'Check the employee',
          message:
            'That employee has no tips or serving shift on this close. Refresh and select the correct employee.',
        };
      case 'validation':
        return {
          title: 'Check the highlighted fields',
          message: err.issues.length
            ? 'Some entries need fixing before this can be saved.'
            : 'Something in this form needs fixing before it can be saved.',
        };
      case 'not_found':
        return {
          title: 'No longer there',
          message: 'This record was removed or changed by someone else. Refresh and try again.',
        };
      case 'supplier_not_found':
        return {
          title: 'That vendor is no longer available',
          message:
            'Refresh the vendor list, then select the vendor again or enter the merchant as a new vendor.',
        };
      case 'insufficient_role':
      case 'missing_token':
        return {
          title: 'Not allowed',
          message: 'You do not have access to make this change. Ask the owner to do it.',
        };
      case 'conflict':
      case 'duplicate':
        return { title: 'Already exists', message: 'Something with these details is already on file.' };
      case 'mutation_conflict':
        return {
          title: 'Something changed first',
          message: 'Refresh the current record, check the latest details, then try your change again.',
        };
      case 'mutation_body_mismatch':
        return {
          title: 'This request no longer matches',
          message:
            'The same save was already started with different details. Refresh the page and make the change again from the current record.',
        };
      case 'fiscal_blocked':
        return {
          title: 'Blocked by invoicing rules',
          message: 'This cannot be changed because it affects an invoice that has already been issued.',
        };
      /*
       * The zero-factura rule, decided by the owner: a bill that charged nothing gets no fiscal
       * document, because reporting a zero sale to the SAR is over-reporting. A zero-charge LINE on
       * a paying bill is different and still prints, at an explicit 0.00.
       */
      case 'check_total_zero':
        return {
          title: 'No factura for a zero bill',
          message:
            'This bill comes to zero — nothing was charged, so no factura is issued and no number is used.',
        };
      case 'quarantine_first':
        return {
          title: 'Quarantine this device first',
          message: 'It may still be holding sales that have not reached the office.',
        };
      /*
       * A PIN is how a register tells one person from the person standing next to them, so two people
       * cannot share one. The server knows whose it clashed with and deliberately will not say —
       * that would be telling a manager one of their staff's PINs — so neither does this.
       */
      case 'pin_in_use':
        return {
          title: 'That PIN is taken',
          message: 'Somebody else here already uses it. Pick different digits.',
        };
      case 'pin_unchanged':
        return {
          title: 'That is already their PIN',
          message: 'Nothing changed. Choose different digits before resetting their PIN.',
        };
      /*
       * Only reachable from Settings, where the owner types the one PIN in this bar that nobody is
       * ever forced to replace. Every other PIN here is generated, and a generated one is never a
       * run or a repeat.
       *
       * NAMES THE SHAPE rather than calling it weak, because the sentence has to be actionable on the
       * second try. The rule is a collision rule before it is a secrecy one: a PIN is unique among
       * active staff at a location, so the first person to take 123456 refuses it to everybody after
       * them — and what they see is "That PIN is taken", which can never say what it clashed with.
       */
      case 'pin_trivial':
        return {
          title: 'Pick different digits',
          message:
            'Not all the same digit, and not a run like 1234 or 4321. Those are the first PINs anybody ' +
            'tries, and the first ones two people end up sharing.',
        };
      case 'not_registered_yet':
        return {
          title: 'It has not been set up yet',
          message: 'Use "Set it up" to get the code, and type that into the register itself.',
        };
      case 'already_registered':
        return {
          title: 'Already set up',
          message:
            'This register is past that step. If it has lost its settings, retire it and add a new one.',
        };
      case 'register_location_mismatch':
        return {
          title: 'Choose a register at this location',
          message:
            'A terminal can only use a cash register at its own location. Choose one from this location, or leave the assignment empty for a KDS.',
        };
      case 'register_has_open_drawer':
        return {
          title: 'This register is still open',
          message:
            'Close and reconcile its current drawer before assigning this terminal to a different register or removing the assignment.',
        };
      /*
       * Retiring a register takes its key away for good, and nothing can hand the same key back. Saying
       * so plainly matters more than it looks: a screen that let this succeed would show a register in
       * service that could never take an order, and the owner would find that out from a bartender.
       */
      case 'credential_revoked':
        return {
          title: 'It cannot be brought back',
          message: 'Retiring a register takes its key away for good. Add a new one to replace it.',
        };
      case 'termination_date_required':
        return {
          title: 'Their last day is missing',
          message:
            'Fill in the day they left. Their final pay is worked out up to that date, and the pay register cannot find them without it.',
        };
      case 'below_threshold':
        return { title: 'Under the minimum', message: 'This is below the payout minimum you set.' };
      case 'no_price':
        return { title: 'No price found', message: 'Nothing is priced for that item at that time.' };
      case 'cycle':
        return { title: 'Circular reference', message: 'That would make a recipe contain itself.' };

      /*
       * THE ONE CODE THAT SHOWS THE SERVER'S OWN SENTENCE, and the exception is deliberate.
       *
       * Everything else in this file maps a code to English written here, so the server can reword a
       * refusal without changing what a manager reads. A price arrangement is refused for reasons
       * that are ABOUT NUMBERS THIS FILE DOES NOT HAVE — what the menu charges for that size, what
       * was typed instead, whose arrangement it is — and the door already writes them as owner copy:
       * four sentences saying what the card charges, why naming more changes nothing, and what to
       * type instead. Rewriting that here as "that price cannot be used" would throw away the only
       * part he can act on, which is the failure this whole file exists to prevent.
       *
       * Safe because the sentences are hand-written prose in `domains/pricing/arrangement.ts` with no
       * identifier, enum literal or internal vocabulary in them — the same reason a pin-drift warning
       * reaches the screen verbatim through `notifyWarnings`. The fallback stands for the day a
       * refusal arrives with nothing to say.
       */
      case 'refused':
        return {
          title: 'Not changed',
          message:
            err.message ||
            'This could not be agreed as typed, and nothing has been changed. Check the price against what the menu charges.',
        };

      /*
       * CHECKED ONCE AND NOT AGAIN — a deposit off the statement, a count against what was expected.
       *
       * Neither of these is about a second person any more. They used to be: the deposit refused
       * whoever had recorded it and the count refused whoever had taken it, and both named somebody
       * who does not exist here. What is left is the only clause that was ever satisfiable — the
       * owner's attestation is written once, with his name and the time on it, and a second press
       * would overwrite it with the same name. So both read as "you have already done this", which
       * is not a fault and must not look like one.
       */
      case 'already_verified':
        return {
          title: 'Already checked off',
          message: 'This one is already recorded as having arrived. Refresh to see when.',
        };
      case 'already_checked':
        return {
          title: 'You have already checked that one',
          message:
            'It carries your name and the time you checked it, and that record does not get written over. Refresh to see what you found.',
        };
      /*
       * Retired rather than refused-for-a-reason, and the wording has to say so or the owner goes
       * looking for the second person the old sentence asked for. There is one bartender on a shift,
       * so nobody was ever there to sign a count at the register; the check is his, from this site.
       */
      case 'count_verification_retired':
        return {
          title: 'Counts are not signed off at the register',
          message:
            'Nobody behind the bar signs another person’s count. Open the drawer here and check it against what it was expected to hold.',
        };
      case 'drawer_is_counted_through_its_shift':
        return {
          title: 'Count this one through its shift',
          message:
            'A cash drawer’s money belongs to the shift it was opened for, so it is counted from the drawer itself. The safe and the float box are counted on their own.',
        };
      case 'business_day_closed':
        return {
          title: 'That night has been closed off',
          message:
            'Its drawers were counted and its takings reported, so nothing new can go on it. Open the night again from Register & cash and say why.',
        };
      case 'drawer_open_conflict':
        return {
          title: 'The opening state changed',
          message:
            'Another register action changed this business day while the drawer was opening. Refresh Cash status and follow the opening step now shown.',
        };
      case 'drawer_still_open':
        return {
          title: 'A drawer is still open',
          message:
            'Complete the closing Drawer Count, reconcile it, and close the shared drawer before closing the business day.',
        };
      case 'ordinary_tabs_still_open':
        return {
          title: 'Tabs still need attention',
          message:
            'Open End of Day, resolve every tab under Tabs to resolve, then try closing the business day again.',
        };
      case 'payment_still_due':
        return {
          title: 'Payment is still needed',
          message:
            'Open End of Day, finish or correct every bill under Payment still needed, then try closing the business day again.',
        };
      case 'close_conflict':
        return {
          title: 'The business day changed',
          message:
            'Refresh End of Day, review the latest closing status, then close it again if it is ready.',
        };

      /*
       * Cash going out of a register from the office.
       *
       * These are the refusals that stop a payment being recorded, and every one of them is a
       * thing the person at the screen has to go and DO something about — open a register, move money
       * into one, say which drawer. Falling through to "try again in a moment" told the owner to
       * retry an action that could never succeed, however many times he pressed it. Measured on
       * 2026-07-28: the gas man's L 3,800.00 bill against a drawer holding L 2,512.00 answered
       * `insufficient_cash_in_drawer` and the screen said "Something went wrong."
       */
      case 'no_open_drawer':
        return {
          title: 'No register is open',
          message:
            'Cash paid out of a register has to come out of a drawer somebody has open. Open the register first, or record it as paid some other way.',
        };
      case 'drawer_session_required':
        return {
          title: 'Say which register',
          message: 'More than one register is open. Pick the one the cash came out of.',
        };
      case 'drawer_session_closed':
        return {
          title: 'That register is already counted off',
          message:
            'It was closed while this was open. Pick a register that is still open, or record it as paid some other way.',
        };
      case 'drawer_session_not_found':
        return {
          title: 'That register is no longer there',
          message: 'Refresh the page and pick the register again.',
        };
      case 'drawer_closed':
        return {
          title: 'This drawer is already closed',
          message: 'The handoff can no longer be changed. Refresh to see the completed drawer session.',
        };
      case 'drawer_handoff_pending':
        return {
          title: 'A handoff is already waiting',
          message:
            'Have the incoming bartender sign in and accept the existing handoff before starting another.',
        };
      case 'drawer_handoff_finished':
        return {
          title: 'That handoff is already finished',
          message: 'Another terminal completed it first. Refresh to see who accepted responsibility.',
        };
      case 'drawer_handoff_needs_incoming_staff':
        return {
          title: 'A different employee must accept',
          message:
            'The outgoing bartender cannot accept their own handoff. Sign in as the incoming bartender.',
        };
      case 'drawer_handoff_not_found':
        return {
          title: 'That handoff is no longer available',
          message: 'Refresh the drawer session and use the handoff currently shown there.',
        };
      case 'insufficient_cash_in_drawer':
        return {
          title: 'Not that much in the drawer',
          message:
            'Take the rest out of the float box or the safe and count it into the register first, or record a smaller part payment.',
        };
      case 'drawer_event_not_found':
      case 'drawer_event_not_a_payout':
        return {
          title: 'No cash went out of a register for that',
          message:
            'Nothing was taken out of a drawer under that record. Record this as a new payment out of the register.',
        };
      case 'drawer_event_currency_mismatch':
        return {
          title: 'That cash was a different money',
          message:
            'What left the drawer was not in this currency. A drawer holds lempiras and dollars separately.',
        };
      case 'drawer_event_overclaimed':
        return {
          title: 'That cash is already spoken for',
          message:
            'What came out of the register that time is already accounted for by other bills. Record this one against its own payment.',
        };

      /*
       * A bill, a credit note, and the arithmetic between them. Each of these names a fact about
       * the paperwork that the person is holding, so each gets the sentence that tells them which
       * piece of paper to look at rather than which button to press again.
       */
      case 'overpayment':
        return {
          title: 'More than this bill is for',
          message: 'Check the amount. A little over is fine — rounding up to the note — but not this much.',
        };
      case 'credit_exceeds_bill':
        return {
          title: 'More than is left on that bill',
          message:
            'A credit cannot take a bill below nothing owed. Lower it, or record it as a credit sitting with them and put it against their next bill.',
        };
      case 'invoice_settled':
        return {
          title: 'Nothing is left owing on that bill',
          message: 'Record this as a credit sitting with them, and put it against their next bill.',
        };
      case 'supplier_mismatch':
        return { title: 'That bill is another vendor’s', message: 'Pick a bill from this same vendor.' };
      case 'currency_mismatch':
        return {
          title: 'Two different monies',
          message: 'This and the bill it goes against have to be in the same currency.',
        };
      case 'not_a_bill':
        return { title: 'That is a credit, not a bill', message: 'A credit cannot come off another credit.' };
      case 'not_a_credit':
        return { title: 'That is a bill, not a credit', message: 'Only a credit can be put against a bill.' };
      case 'already_applied':
        return { title: 'Already against a bill', message: 'This credit has already been put against one.' };
      /*
       * The photographed bill. Each of these is about a FILE the person just chose or a file that
       * is not there any more, and the difference between the last two is the whole reason the
       * feature exists: "nobody took a picture" and "there was a picture and it has gone" send a
       * person to two different places.
       */
      case 'unsupported_type':
        return {
          title: 'That is not a photo or a PDF',
          message: 'Pick the picture you took of the paper, or a PDF the supplier sent.',
        };
      case 'too_large':
        return {
          title: 'That file is too big',
          message: 'The limit is 10 MB. Choose a smaller photo or PDF.',
        };
      case 'not_a_file':
        return {
          title: 'That file did not arrive',
          message: 'Nothing was attached. Choose the file again.',
        };
      case 'document_gone':
        return {
          title: 'That photograph is no longer stored',
          message:
            'It was attached to this record and the file itself has gone. If you still have the paper, photograph it again and attach the new one.',
        };

      case 'due_before_invoice':
        return {
          title: 'It falls due before it was written',
          message: 'The day it has to be paid by cannot be earlier than the date on the bill.',
        };

      /*
       * PUTTING A DELIVERY RIGHT, and the four things that stop it.
       *
       * Every one of these is a refusal the server writes a door into: it has already worked out
       * that arithmetic cannot fix this and named the thing that can. None of them was registered
       * here when the correction shipped, so all four fell through to "Could not correct this
       * delivery. Try again in a moment." — measured on the real screen against a bill the vendor
       * had already credited, and it is precisely the mistake the cash-drawer block above records
       * having made a day earlier: telling the owner to retry an action that can never succeed,
       * however many times he presses it.
       */
      case 'already_corrected':
        return {
          title: 'This one has already been put right',
          message:
            'A later delivery replaced it. Open that one from this page and correct it instead — it is the one everything counts.',
        };
      case 'bill_already_paid':
        return {
          title: 'This delivery’s bill has been paid',
          message:
            'Money has already gone out against it, so the bill cannot be restated. Ask the supplier for a credit note for the difference and record it against that bill.',
        };
      case 'bill_already_credited':
        return {
          title: 'They have already credited part of this bill',
          message:
            'The bill has been changed once by the vendor, so it cannot be restated here. Record what they credit for the rest against that bill.',
        };
      case 'sold_since_it_arrived':
        return {
          title: 'More has gone out than this says arrived',
          message:
            'The shelf cannot give back stock that has already left the building. Count that area and write off what is missing.',
        };
      case 'storage_location_required':
        return {
          title: 'Say where the goods went',
          message: 'This delivery never put anything away, so there is nothing to read a shelf back off.',
        };

      /*
       * WHAT IS ACTUALLY GOING ON THE SHELF, and the five ways a line cannot.
       *
       * These are the delivery door's own refusals about the goods, and they were unreachable from
       * a screen for exactly as long as the office form worked all five out for itself before it
       * posted. Then a CORRECTION started going through the same rules — same table, same shelf —
       * and that form does not: it carries the vendor's own lines forward, so an item deleted since
       * the delivery, a case size taken off the item, or a shelf that has been moved to another site
       * all arrive here. Unspoken they read as "Try again in a moment" about something that can
       * never succeed.
       */
      case 'item_has_no_unit':
        return {
          title: 'That item is not counted in anything yet',
          message:
            'Fill in "Counted in" for it under Stock items — without it there is nothing to put the amount on the shelf in.',
        };
      case 'item_not_at_this_location':
        return {
          title: 'That item belongs to another of your places',
          message:
            'Stock, costs and counts are kept per place, so it cannot go on a shelf here. Pick this place’s own version of it.',
        };
      case 'storage_location_not_here':
        return {
          title: 'That shelf is not at this place',
          message: 'Check where the goods were put away and pick somewhere in this building.',
        };
      case 'too_little_to_count':
        return {
          title: 'Less than the shelf can hold',
          message:
            'Converted into the unit this item is counted in, that amount rounds away to nothing. Record it in a unit the shelf can carry.',
        };
      case 'no_lines':
        return {
          title: 'Nothing on this delivery',
          message: 'Say what arrived — a delivery needs at least one line.',
        };
      case 'already_billed_needs_no_paper':
        return {
          title: 'These goods were billed earlier',
          message:
            'They came off a bill that already exists, so this delivery has no invoice total of its own and nothing to disagree with.',
        };
      case 'more_than_was_short':
        return {
          title: 'More than that bill was short of',
          message:
            'Only what they billed for and did not send can arrive against it. Anything beyond that needs a delivery of its own.',
        };
      case 'line_currency_mismatch':
        return {
          title: 'Two different monies on one bill',
          message: 'Everything on one invoice has to be priced in the money the invoice is in.',
        };
      case 'manual_share_mismatch':
        return {
          title: 'The split does not add up',
          message: 'The amounts entered by hand have to come to exactly what the freight was.',
        };

      /*
       * ============================================================================================
       * EVERY OTHER REFUSAL AN OFFICE SCREEN CAN MEET.
       *
       * The three blocks above each record the same discovery twice: a rule the server enforces
       * perfectly, worded on the server in a sentence that says which piece of paper to look at,
       * reaching the owner as "Something went wrong. Try again in a moment." Each time it was found
       * by somebody pressing the button, and each time the fix was the codes that had just been
       * measured — never the ones that had not.
       *
       * So it happened again, on the two newest rules in the platform and on the same afternoon
       * they shipped. Measured on the real screens on 2026-07-29:
       *
       *   - the safe on Register & cash reads -$250.00 under a sentence promising it "cannot be
       *     taken below nothing". Recording a deposit out of it answers `insufficient_cash_in_box`
       *     — *"there is not that much in Safe — record how the money got there before it goes out
       *     again"* — and the screen said "Could not record the deposit. Try again in a moment."
       *   - "Sign this register off", the whole of the pay-register work, answered
       *     `register_predates_cadence` — *"this register was worked out before pay periods were
       *     taken into account, so a salary on it may be a whole month too high — build it again,
       *     then sign it off"* — and the screen said "Could not sign the register off. Try again in
       *     a moment." That refusal is the guard standing between the owner and paying every
       *     salaried person double, and it read as a glitch.
       *
       * Sixty-seven codes were in that state. This block is all of them, and
       * `scripts/check-refusal-copy.mjs` now fails the build when a sixty-eighth appears, because
       * three rounds of finding them by hand is enough.
       *
       * The rule for the wording is the one at the top of this file: say what happened and what to
       * do about it, never the status, never the server's prose. Where the server's sentence names
       * a fact this file cannot know — which supplier, which CAI, how many items — the copy points
       * at the place that fact is written down instead of guessing at it.
       * ============================================================================================
       */

      /*
       * THE CASH ROOM. A box handing over money it is not recorded as holding, a transfer that
       * cannot be made, a count that needs a second pair of eyes. Every one of these is the same
       * shape as `insufficient_cash_in_drawer` above — the person has to go and DO something, and
       * pressing the button again is the one thing that can never work.
       */
      case 'insufficient_cash_in_box':
        return {
          title: 'Not that much in that cash box',
          message:
            'Nothing on file has put that much into it. Record the drop or the transfer that brought the cash in, and this will go through — a box can only hand over what it has been recorded as holding.',
        };
      case 'transfer_is_empty':
        return {
          title: 'Say how much is moving',
          message: 'A transfer with no money on it records nothing.',
        };

      /*
       * A BILL PAID STRAIGHT OUT OF A BOX — the water man at eleven in the morning, settled from
       * the float box with no register open. Three of these four are shapes the payment form does not
       * offer, so they should never be read off a screen; the sentences still say which door to
       * use, because a refusal that only a stale page can meet is exactly the one that gets read
       * with no comment beside it.
       */
      case 'cash_box_required':
        return {
          title: 'Say which box the cash came out of',
          message:
            'A payment out of a box has to name the box — the float box or the safe. Choose one and record it again.',
        };
      case 'drawer_pays_out_through_its_register':
        return {
          title: 'A drawer pays out through its shift',
          message:
            'Cash out of a register drawer belongs to the shift that is open on it — record it as cash out of the register. Paying out of a box is for the float box and the safe.',
        };
      case 'box_payment_names_no_movement':
        return {
          title: 'A box payment records its own movement',
          message:
            'Pointing a payment at a movement the register already made is for cash out of a register. A payment out of a box writes its own record — leave the register movement off and record it again.',
        };
      case 'float_is_counted_in_notes':
        return {
          title: 'That float is stated note by note',
          message:
            'This box’s standing float is a list of notes, and its totals are worked out from them — a total cannot be typed over it. Change the notes with “Set what it holds” on the Register & cash screen, and the totals follow.',
        };

      /*
       * BREAKING A NOTE AGAINST THE FLOAT BOX. The two sides of a swap are the same money in
       * different notes, so a swap that does not balance is not a swap at all — the drawer really is
       * keeping some of it, which is a movement and has its own door. The sentence says which door,
       * because "does not balance" on its own reads as a bug rather than as a decision.
       */
      case 'exchange_does_not_balance':
        return {
          title: 'The two sides do not come to the same amount',
          message:
            'Breaking notes changes what the drawer is holding, never how much. If some of it is staying in the drawer or going into the float box for good, move it instead — that is a transfer, and it is meant to change the balance.',
        };
      case 'exchange_is_empty':
        return {
          title: 'Say which notes were swapped',
          message: 'An exchange with no notes on either side records nothing.',
        };
      case 'not_the_change_box':
        return {
          title: 'Notes are broken against the float box',
          message:
            'The float box is where small notes are kept for change. The safe holds the night’s takings and is not opened to break a five hundred.',
        };

      /*
       * TIPS LEFT ON CARDS. The ceiling here is the tip ledger and not a permission — the money is
       * already somebody's — so both refusals are about the LEDGER, and both sentences say what it
       * currently holds rather than merely saying no.
       */
      case 'more_than_the_tips_owed':
        return {
          title: 'More than is owed in card tips',
          message:
            'Only tips a guest actually left on a card can be handed over from the drawer. Check what is outstanding for that person and hand over that, or less.',
        };
      case 'no_card_tips_owed':
        return {
          title: 'Nothing outstanding for that person',
          message:
            'Every card tip they have earned has already been handed over. Cash tips are declared at the end of the night and never come out of the drawer.',
        };
      case 'staff_not_found':
        return {
          title: 'Nobody here by that name',
          message: 'That person is not on the roster for this bar. Check the staff list and try again.',
        };
      case 'customer_link_conflict':
        return {
          title: 'One link per person',
          message:
            'A customer can be one of your staff or one of the dive shop’s people, not both. Clear one link before setting the other.',
        };
      case 'customer_staff_link_taken':
        return {
          title: 'They already have a customer record',
          message:
            'That staff member is already linked to a customer. Edit that record instead of making a second one for the same person.',
        };
      case 'house_account_not_owner':
        return {
          title: 'The house account is the owner’s',
          message:
            'Only the owner’s own record carries the house account — his PIN is what puts a bill on the house. An employee’s meals go through the staff meal discount instead.',
        };
      case 'house_tab_discount_protected':
        return {
          title: 'The house tab depends on this one',
          message:
            'This is how the owner’s tab is settled at close — switching it off or changing its value would leave every open house tab unable to close. Renaming it is fine.',
        };
      case 'transfer_to_itself':
        return {
          title: 'It has to go somewhere else',
          message: 'Pick a different cash box for the money to move into.',
        };
      case 'transfer_between_drawers':
        return {
          title: 'Cash does not go straight from one drawer to another',
          message:
            'It goes drawer, float box, drawer — and it is counted in on the way through, so each drawer’s count still stands on its own.',
        };
      case 'deposit_is_empty':
        return {
          title: 'Say how much went to the bank',
          message:
            'A deposit with no money on it records nothing. Fill in the dollars, the lempiras, or both.',
        };
      /*
       * FOUR SENTENCES ABOUT A WITNESS USED TO STAND HERE AND THEY ARE DELETED RATHER THAN REWORDED.
       *
       * `verifier_is_counter` ("Ask somebody else to witness it"), `verifier_not_permitted` ("Pick
       * somebody else"), `count_already_verified` ("Somebody has already checked this count") and
       * `closing_count_unverified` ("Somebody other than whoever counted it has to check it before
       * this drawer can be closed off") were the portal's half of a control that named a second
       * bartender. There is one bartender on a shift; nothing raises any of the four now.
       *
       * Rewording them would have been the wrong repair. Copy in this file exists so a refusal the
       * server can send reads as something a person can act on — an entry for a code that can never
       * arrive is not reassurance, it is a promise on the owner's screen that a control he does not
       * have is watching for him. What replaced them is `count_verification_retired` above and
       * `count_already_checked` below, and both describe his own check rather than somebody else's.
       */
      case 'count_superseded':
        return {
          title: 'There is a newer count',
          message:
            'This one was replaced by a recount. Check the new one instead — it is the one that counts.',
        };
      case 'count_not_found':
        return { title: 'That count is no longer there', message: 'Refresh the page and pick it again.' };
      /*
       * The one refusal on the register screen that is a rule rather than a mishap, so it says what
       * the rule is FOR. Moving a drawer changes what the bartender will be counted off against with
       * no cash having moved; the sentence beside it is the only account anybody will have of that
       * later, and "required field" would read as the software being fussy.
       */
      /*
       * Two tabs open on the same drawer, or a second press before the first answer came back. It is
       * not a fault and it must not read as one — the check happened, and the only thing missing on
       * screen is whose name went on it, which a refresh answers.
       */
      case 'count_already_checked':
        return {
          title: 'Already signed off',
          message: 'That count has your name and the time on it. Refresh to see it.',
        };
      case 'adjustment_needs_reason':
        return {
          title: 'Say why first',
          message:
            'Moving the drawer changes what the register is counted off against tonight. Write down what you think happened — it stays on the count with your name, and it is all anybody will have to go on later.',
        };
      /*
       * Pressed twice, or two tabs open on the same count. The drawer moved once and must not be
       * walked down again by every extra click, so this is a statement of what already happened
       * rather than a fault.
       */
      case 'drawer_already_moved':
        return {
          title: 'Already moved',
          message:
            'The drawer has been moved onto that count once, with your reason on it. Refresh to see where it stands now; count it again if you want to move it further.',
        };
      /*
       * The safe and the change box have no shift to move and their own count IS the correction —
       * everything after it is added to what was found. So this is not "not allowed", it is "there
       * is nothing here that needs it".
       */
      case 'count_is_not_a_drawer_count':
        return {
          title: 'Nothing to move',
          message:
            'That is a count of the safe or the change box, and counting one is already the correction: the balance now reads what you found, and everything that moves afterwards is added to it.',
        };
      /*
       * A SEPARATE CODE FROM THE ONE ABOVE, and the split is the point of it. A cash drawer counted
       * with the bar shut carries no shift either, and the sentence above would tell the owner his
       * register is a box. Same verdict — nothing to move — for a completely different reason, and the
       * reason is the one that matters to him, because it says what the count he just took is FOR.
       */
      case 'count_taken_between_shifts':
        return {
          title: 'Nothing to move',
          message:
            'Nobody had that register open when you counted it, so there is no running total to correct — the next shift starts from its own opening count. What you found is on the record with the time and your name on it, which is what puts the reason beside tomorrow morning’s opening difference instead of on whoever opens the register.',
        };
      /*
       * The float going IN is measured against the standard float — "is this float what it is
       * supposed to be" — while the register is already expected to hold whatever was counted into it.
       * So that difference is not a difference from the drawer, and moving by it walks the drawer
       * down twice and reports the shift over at close. There is a real remedy and this names it.
       */
      case 'count_is_the_opening_float':
        return {
          title: 'That is the float going in',
          message:
            'An opening count is measured against the standard float, and the register is already expected to hold exactly what was counted into it — so there is no difference here to move the drawer by. If the float itself was miscounted, it can be counted again from the register before anything else moves through the drawer.',
        };
      case 'count_measured_against_nothing':
        return {
          title: 'Nothing to move it by',
          message:
            'Nothing had ever been recorded going into that register when it was counted, so there is no difference to move the drawer by. What you found is on the record either way.',
        };
      /*
       * The two doors for counting cash, each pointing at the other. Both are directions rather than
       * refusals of the gesture: the money can be counted, and this says where.
       */
      case 'count_a_drawer_as_a_drawer':
        return {
          title: 'Count it as a cash drawer',
          message:
            'A cash drawer is measured against the shift that left the money in it, not against a box balance. Count it from the registers on the register screen.',
        };
      case 'not_a_register_drawer':
        return {
          title: 'That one is a box, not a register',
          message:
            'The safe and the change box are measured against their own balance. Count either of them from the cards under On hand.',
        };
      case 'a_shift_is_open_on_that_drawer':
        return {
          title: 'That register is trading',
          message:
            'A shift is open on it, so what it should be holding is moving with every sale. Open the shift and pull the drawer there — that is also the only place the drawer can be moved onto what you count.',
        };
      case 'no_closing_count':
        return {
          title: 'Count the drawer first',
          message:
            'A drawer is counted before it is closed off, or its over and short can never be worked out.',
        };
      case 'float_already_traded':
        return {
          title: 'Money has already moved through this drawer',
          message: 'Its opening float cannot be restated now. Count it as it stands instead.',
        };
      case 'drawer_already_open':
        return {
          title: 'That register already has a drawer open',
          message: 'Count off the drawer that is open before opening another on the same register.',
        };
      case 'drawer_not_on_that_register':
        return {
          title: 'That drawer is not this register’s',
          message: 'Pick the drawer this register counts into.',
        };
      case 'cash_location_not_found':
        return {
          title: 'No such cash box',
          message: 'It has been removed or renamed. Refresh the page and choose again.',
        };
      case 'cajaita_not_found':
        return {
          title: 'The cajaita is not set up',
          message:
            'Add the cajaita under Cash locations before asking the bartender to complete the daily opening check.',
        };
      case 'cajaita_count_not_found':
        return {
          title: 'That Cajaita Count is no longer there',
          message: 'Return to End of Day, refresh the Cajaita status, and open the current count.',
        };
      case 'cajaita_count_superseded':
        return {
          title: 'There is a newer Cajaita Count',
          message:
            'Return to End of Day and review the latest count instead; it is the one used for closing.',
        };
      case 'cajaita_acknowledgement_conflict':
        return {
          title: 'The Cajaita Count changed',
          message:
            'Return to End of Day, refresh the Cajaita status, and review the current count before continuing.',
        };
      case 'cajaita_has_no_discrepancy':
        return {
          title: 'Cajaita already matches',
          message:
            'No variance needs acknowledgment. Return to End of Day and continue with the current status.',
        };
      case 'cajaita_discrepancy_unacknowledged':
        return {
          title: 'Review the Cajaita difference',
          message:
            'Open the current Cajaita Count, recount it, and either correct the count or acknowledge the remaining variance before continuing End of Day.',
        };
      case 'owner_not_terminable':
        return {
          title: 'That row is you',
          message:
            'The owner does not leave their own bar, so this row cannot be terminated. If you meant to end ' +
            'someone’s employment, open their page from the staff list.',
        };
      case 'owner_pin_elsewhere':
        return {
          title: 'Your PIN is set from Settings',
          message:
            'The owner’s register PIN is not set from the staff pages. Go to Settings and use “Your PIN at ' +
            'the register.”',
        };
      case 'register_not_assigned':
        return {
          title: 'That terminal has no register assigned to it',
          message:
            'A drawer is opened against a register, and this terminal does not say which one it is. Set the ' +
            'register on the device under Terminals, then try again.',
        };
      case 'register_not_found':
        return {
          title: 'That register is no longer there',
          message: 'Refresh the page and pick the register again.',
        };
      case 'register_has_no_drawer':
        return {
          title: 'That register has no drawer',
          message: 'Point it at a cash drawer under Register & cash before anybody opens a shift on it.',
        };
      case 'unknown_denomination':
        return {
          title: 'That is not a note this bar takes',
          message: 'One of the amounts on the count sheet is not a note or coin on the list. Check it.',
        };
      case 'unknown_reason_code':
        return {
          title: 'That is not one of the reasons on the list',
          message: 'Pick one of the reasons offered, or add it under Settings first.',
        };

      /* THE TRADING NIGHT. `business_day_closed` is above, with the deposit; these two are what a
         second person closing or reopening the same night looks like from here. */
      case 'day_already_closed':
        return {
          title: 'That night is already closed off',
          message: 'Somebody closed it while this screen was open. Refresh to see when, and who by.',
        };
      case 'day_not_closed':
        return {
          title: 'That night is still trading',
          message: 'It has not been closed off, so there is nothing to open again.',
        };

      /*
       * THE PAY REGISTER. The first of these is the reason the whole approve/pay flow exists: it is
       * the guard between the owner and paying every salaried person a whole month twice, and it
       * spent its first day on the screen reading "Something went wrong".
       */
      case 'register_predates_cadence':
        return {
          title: 'This register was worked out the old way',
          message:
            'It was built before pay periods were taken into account, so a salary on it may be a whole month too high. Build it again, then sign it off.',
        };
      case 'register_employment_changed':
        return {
          title: 'The employment dates changed',
          message:
            'Someone started or left after these figures were calculated. Rebuild the register so its people and part-period salaries use the current dates, then sign it off.',
        };
      case 'register_not_draft':
      case 'run_locked':
        return {
          title: 'This register has already been signed off',
          message:
            'Open it again first if it has to change — a signed-off register is not rebuilt underneath anyone.',
        };
      case 'register_is_empty':
        return {
          title: 'There is nobody on this register',
          message: 'Nothing was worked out for this period. Approve the hours, then build it again.',
        };
      case 'register_not_finalized':
        return {
          title: 'Sign it off first',
          message: 'A register is signed off before it is marked paid.',
        };
      case 'register_already_paid':
        return {
          title: 'This register is already marked paid',
          message:
            'Paid does not come back. Anything still wrong belongs on the next period, as a correction.',
        };
      case 'period_closed':
        return {
          title: 'That pay period is closed',
          message: 'It has been paid off, so nothing more can be built for it.',
        };
      case 'owner_not_on_payroll':
        return {
          title: 'The owner is not on payroll',
          message:
            'You sign in like staff, but there is no employment behind it — nothing here to pay or adjust.',
        };
      case 'register_locked':
        return {
          title: 'The register for that period is signed off',
          message:
            'Its figures have stopped moving, so what feeds them cannot change either. Put the register back to a draft first, then rebuild it after the change.',
        };
      case 'bad_paid_at':
        return {
          title: 'That payday cannot be right',
          message:
            'Paid records something that has already happened — pick the day the money actually went out, today or earlier.',
        };
      case 'already_approved':
        return {
          title: 'These hours are already approved',
          message: 'Somebody approved them while this screen was open. Refresh to see them as they stand.',
        };
      case 'nothing_to_change':
        return {
          title: 'Nothing on that punch was changed',
          message: 'Say what it should be — a different time, a different kind, or the break flag.',
        };
      case 'invalid_period_dates':
        return {
          title: 'Those dates do not make a pay period',
          message:
            'The end has to fall after the start, and the two together have to cover the days you pay for.',
        };
      /* A working week, as it is typed on the schedule form. */
      case 'invalid_weekday':
        return { title: 'Pick a day of the week', message: 'A shift belongs to one named day.' };
      case 'invalid_time':
        return { title: 'That is not a time of day', message: 'Use the 24-hour clock — 07:00, or 16:30.' };
      case 'invalid_shift_window':
        return {
          title: 'A shift cannot start and finish at the same moment',
          message:
            'Equal times are either no shift at all or a whole day, and neither is what anybody meant.',
        };
      case 'invalid_break':
        return {
          title: 'That break is not a number of minutes',
          message: 'Put in whole minutes, or nothing.',
        };
      case 'break_exceeds_shift':
        return {
          title: 'The break is longer than the shift',
          message:
            'Unpaid break comes out of the hours worked, so it has to be shorter than the shift itself.',
        };

      /* A DAY WRITTEN BY HAND — hours the clock never saw, or a paid day off. */
      case 'bad_entry_date':
        return {
          title: 'That is not a calendar day',
          message: 'Pick the day the hours belong to from the calendar, and try again.',
        };
      case 'times_required':
        return {
          title: 'When did the day start and end?',
          message:
            'A worked day is recorded as its clock-in and clock-out, not a total. Put in both times — an end earlier than the start means the shift ran past midnight.',
        };
      case 'minutes_not_allowed':
        return {
          title: 'Worked days are recorded as times now',
          message:
            'Put in the clock-in and clock-out instead of a total. The times are what the pay is worked out from.',
        };
      case 'empty_shift':
        return {
          title: 'That shift starts and ends at the same moment',
          message: 'Equal times make a shift of nothing, which is a typo, not a day. Check the clock-out.',
        };
      case 'break_swallows_shift':
        return {
          title: 'The break swallows the whole shift',
          message:
            'The unpaid break has to be shorter than the shift it comes out of. Check the break minutes against the times.',
        };
      case 'times_not_allowed':
        return {
          title: 'A day off has no clock times',
          message:
            'PTO pays the scheduled day, or the hours you give it — there is no clock-in, clock-out or break to record. Clear the times and save again.',
        };
      /*
       * A WHOLE PAY PERIOD SENT AS ONE SHEET, and the day it was refused over.
       *
       * The sheet is all or nothing, so every one of these means nothing at all was written and
       * nothing typed was lost. Each names the cell to look at rather than the request, because on
       * a grid of two hundred days "the save was refused" is not something anybody can act on.
       */
      case 'duplicate_entry':
        return {
          title: 'That day is on the sheet twice',
          message:
            'One person’s day has one answer, and two would mean one of them was thrown away without anybody being told. Refresh the sheet so each day appears once, and enter it again.',
        };

      /*
       * A DAY ENTERED AS HOURS ALONE — the months worked before anybody was on the clock.
       *
       * Which days may be written that way is the owner's own answer: the date his staff came onto
       * the clock, which moves as he brings them on and which nothing in this software can know.
       * All three sentences point at that date rather than at the box, because the box is doing
       * exactly what it was told and the date is the only thing that can change the answer.
       */
      case 'minutes_required':
        return {
          title: 'How many hours were worked?',
          message:
            'A day entered without times is the hours worked and nothing else, so it needs the hours. Put them in, or record the day as its clock-in and clock-out instead.',
        };
      case 'totals_only_not_open':
        return {
          title: 'No day can be entered without times yet',
          message:
            'Nothing says when your staff came onto the clock, so every day has to be recorded as its clock-in and clock-out. Set that day on the hours sheet, under “Backfilling months nobody punched?”, and days before it can be entered as hours alone.',
        };
      case 'totals_only_closed':
        return {
          title: 'That day is on the clock',
          message:
            'It falls on or after the day you said your staff came onto the clock, so it is recorded as a clock-in and a clock-out. Put the times in for this one, or move that day on the hours sheet.',
        };
      case 'totals_only_stands_alone':
        return {
          title: 'Hours with no times carry nothing else',
          message:
            'A day entered as hours alone is the whole record of that day — no clock-in, no clock-out, no break. Clear the times, or record the day as its times instead, which is the better record wherever you have them.',
        };

      /*
       * TAX SETS. Retiring one is deliberately refused while anything is priced in it — the two
       * halves of "not in use" are that a set with items cannot be retired and a retired set cannot
       * be filled up again, and only the pair of them means anything.
       */
      case 'tax_group_in_use':
        return {
          title: 'Items are still in this set',
          message:
            'Put them in another set first. Retiring this one would leave them priced with no charge behind them.',
        };
      case 'tax_group_retired':
        return {
          title: 'That tax set is not in use',
          message: 'Choose one that is, or bring this one back into use first.',
        };
      case 'charge_code_in_use':
        return {
          title: 'That short code is taken',
          message: 'This set already has a charge using it. Pick different letters.',
        };

      /* THE VENDOR, AND WHAT ARRIVED AGAINST WHAT WAS BILLED. */
      case 'supplier_name_taken':
        return {
          title: 'That supplier is already on file',
          message:
            'Open the one that exists instead. A second row under the same name splits what they have cost you between the two.',
        };
      case 'not_a_goods_supplier':
        return {
          title: 'That company only carries goods',
          message:
            'The ferry and the forwarders bring things over; what they charge is a shipping charge, recorded under Shipping over. Pick somewhere you buy from.',
        };
      case 'not_a_carrier':
        return {
          title: 'That supplier is not a shipping company',
          message: 'A crossing is billed by whoever carried it. Pick the ferry or the forwarder.',
        };
      case 'branch_not_this_supplier':
        return {
          title: 'That place belongs to another supplier',
          message: 'Pick one of this supplier’s own places.',
        };
      case 'item_twice':
        return {
          title: 'The same item is on this list twice',
          message:
            'Both prices would be recorded and one would quietly replace the other, with nothing to say which was meant. Leave the line you want.',
        };
      case 'unknown_item':
        return {
          title: 'That is not something you hold in stock',
          message: 'Add it as a stock item first, or take the line off this price list.',
        };
      case 'receipt_not_found':
        return {
          title: 'One of those deliveries is not there',
          message: 'It may have been corrected or removed since. Refresh and pick the deliveries again.',
        };
      case 'no_discrepancy':
        return {
          title: 'That bill agrees with its delivery',
          message:
            'What they billed and what arrived come to the same figure, so there is nothing to explain.',
        };
      case 'no_open_discrepancy':
        return {
          title: 'That difference has already been dealt with',
          message: 'Refresh to see how it was settled.',
        };
      case 'discrepancy_unexplained':
        return {
          title: 'Say what the difference is',
          message:
            'Their invoice and the goods that arrived do not come to the same figure, and this cannot be recorded until somebody says why.',
        };
      case 'discrepancy_reason_mismatch':
        return {
          title: 'That reason cannot be true of this bill',
          message:
            'It describes the difference the other way round. Check whether they billed for more than arrived, or less.',
        };
      case 'already_reversed':
        return {
          title: 'That payment has already been taken back',
          message: 'Refresh to see the row that undid it — both stay on the list.',
        };
      case 'payment_is_a_reversal':
        return {
          title: 'That row is an undo, not a payment',
          message: 'It is the row that took another payment back. Record a new payment instead.',
        };

      /* THE SHELF. */
      case 'count_already_open':
        return {
          title: 'Something here is already on a count nobody has approved',
          message:
            // It now says WHERE. This sentence used to tell the reader to approve or discard the
            // open count, and there was no screen in the portal from which either was possible —
            // so the area stayed uncountable and the instruction was a dead end.
            'Deal with the open count of this area first, under "Counts waiting for you" on the On hand tab. The same item on two sheets is how a shelf ends up wrong and nobody can say which sheet did it.',
        };
      case 'count_already_posted':
        return {
          title: 'That count has already gone through',
          message:
            'Its differences are on the shelf figures already. Count the area again if something still looks wrong — a posted movement cannot be taken back.',
        };
      case 'count_was_discarded':
        return {
          title: 'That count was thrown away',
          message:
            'Its figures were never used and the area has been free to count since. Count it again if the shelf still needs correcting.',
        };
      case 'invalid_room':
        return {
          title: 'That does not describe a place in the building',
          message: 'Check the room, the shelf, and what it sits inside.',
        };
      case 'unit_not_convertible':
        return {
          title: 'Nothing says how those two units compare',
          message:
            'This item is counted in one unit and the amount is written in another. Set the conversion on the item first, or write the amount in the unit it is counted in.',
        };

      /* INVOICING. A range, a number, and the paper itself. */
      case 'device_already_has_range':
        return {
          title: 'That register already prints from another range',
          message:
            'Stop the range it is on before attaching this one. A register printing from two ranges leaves a gap in both, and a gap is a conversation with the SAR.',
        };
      case 'correlativo_conflict':
        return {
          title: 'That number was taken a moment ago',
          message: 'Nothing was issued and no number was used up. Try again — it will take the next one.',
        };
      // Its sibling above is two notes racing for one NUMBER; this is two notes racing against one
      // BILL, and the commission each would give back collides. Same answer, because the same thing
      // is true of both: the batch rolled back whole, so nothing was issued and nothing was taken
      // off what the partner is owed. Worth its own words rather than folding into the one above —
      // an owner told "that number was taken" while looking at a bill he is refunding goes looking
      // for a numbering problem that is not there.
      case 'correction_conflict':
        return {
          title: 'Another correction against this bill landed first',
          message:
            'Nothing was issued and no number was used up. Try again — this one will be worked out on top of the correction that landed.',
        };
      case 'not_numbered':
        return {
          title: 'This document has no number',
          message: 'Only a numbered document is kept on the shelf.',
        };
      case 'pdf_not_ready':
        return {
          title: 'Still being prepared',
          message: 'The document is being made now. Give it a moment and open it again.',
        };

      /* ROLES AND THE PEOPLE IN THEM. A staff role is what a register will let somebody do, not a
         portal login — nobody signs in to this site with one. */
      case 'unknown_role':
        return {
          title: 'No such role',
          message: 'It may have been renamed or removed since this page was opened. Refresh and pick again.',
        };
      case 'unknown_staff':
        return {
          title: 'That person is not on the staff list',
          message: 'They may have been removed since this page was opened. Refresh and pick again.',
        };
      case 'unknown_permission':
        return {
          title: 'That permission is not on the list',
          message: 'Refresh the page — what a role can be allowed to do has changed since it was opened.',
        };
      case 'duplicate_permission':
        return {
          title: 'That permission is listed twice',
          message: 'Each one is allowed once on a role.',
        };
      case 'grant_not_meaningful':
        return {
          title: 'That does not mean anything on this permission',
          message: 'A ceiling or an approver only makes sense on some of them. Leave it off this one.',
        };
      case 'seeded_role':
        return {
          title: 'This role is built in',
          message: 'Its name cannot be changed. Add a role of your own if you need a different one.',
        };

      /* THE ACCOUNTANT'S MONTHS. */
      case 'month_already_filed':
        return {
          title: 'That month is already filed',
          message: 'Refresh to see when it was filed, and who marked it.',
        };

      /* THE ACCOUNTS REGULARS SETTLE LATER. */
      case 'account_not_allowed':
        return {
          title: 'They do not have an account',
          message:
            'Turn on "put bills on their account" on their customer record first — an account is trust you extend by name.',
        };
      case 'account_over_limit':
        return {
          title: 'That would put them past their limit',
          message:
            'The charge would take their account past the cap you set for that currency. Take a payment first, or raise the limit on their customer record.',
        };
      case 'customer_retired':
        return {
          title: 'That customer is retired',
          message:
            'A retired customer can still pay what they owe, but cannot take on new charges. Bring them back on the Customers page if they are drinking here again.',
        };

      /* THE STAFF MEAL'S ELIGIBLE LIST. */
      case 'discount_not_staff_benefit':
        return {
          title: 'That discount does not carry a covered-items list',
          message:
            'Only the staff-meal discount reads one. Mark the discount as a staff benefit first, or edit the staff meal instead.',
        };
      case 'eligible_rules_duplicate':
        return {
          title: 'The same item is named twice',
          message: 'Each category or item takes one entry on the list. Remove the duplicate and save again.',
        };
      case 'eligible_target_unknown':
        return {
          title: 'That is not on the menu',
          message:
            'A category or item on the list may have been removed since this page was opened. Refresh and pick again.',
        };

      default:
        break;
    }

    if (err.status === 401) {
      return { title: 'Signed out', message: 'Your session expired. Sign in again to continue.' };
    }
    if (err.status === 403) {
      return {
        title: 'Not allowed',
        message: 'You do not have access to make this change. Ask the owner to do it.',
      };
    }
    if (err.status >= 500) {
      return {
        title: 'Something went wrong',
        message: `Nothing was saved${action ? ` while trying to ${action}` : ''}. Try again in a moment.`,
      };
    }
  }

  return {
    title: 'Something went wrong',
    message: action ? `Could not ${action}. Try again in a moment.` : 'Try again in a moment.',
  };
}

/** Issues keyed by field, ready for `form.setErrors`. */
export function fieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError)) return {};
  return Object.fromEntries(err.issues.map((i) => [i.field, i.message]));
}
